/**
 * Rutas de platform — transversal: landing, primeros pasos, bandeja, datos, insights, configuración, roles y el catch-all.
 *
 * Se componen en `src/pages/index.jsx`. Es un fragmento, no un array: React Router
 * recorre los `<React.Fragment>` hijos de `<Routes>`, así que el JSX se mudó tal cual
 * —sin `key` ni comas— y el diff es el movimiento, nada más.
 *
 * `routeTable.test.jsx` congela las 174 rutas de la app contra la tabla previa a la
 * mudanza: perder una ruta aquí no da error, da un 404.
 */
import { Route, Navigate } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { PermissionGuard } from '@/components/auth/PermissionGuard';
import { PERMS } from '@/constants/permissions';
import InviteManager from '@/pages/InviteManager';
import { FirmHomeGate, HomeRoute } from '@/pages/routeHelpers';

const Datos = lazyWithRetry(() => import("@/pages/Datos"));
const BandejaDocumentos = lazyWithRetry(() => import("@/pages/BandejaDocumentos"));
const OcrStats = lazyWithRetry(() => import("@/pages/OcrStats"));
const PrimerosPasosPage = lazyWithRetry(() => import("@/pages/PrimerosPasosPage"));
const IESEPanel = lazyWithRetry(() => import("@/pages/IESEPanel"));
const InsightsHub = lazyWithRetry(() => import("@/pages/InsightsHub"));
const Configuracion = lazyWithRetry(() => import("@/pages/Configuracion"));
const RolesManagement = lazyWithRetry(() => import("@/pages/RolesManagement"));
const ApprovalsDashboard = lazyWithRetry(() => import("@/pages/ApprovalsDashboard"));
const PrivacyPolicy = lazyWithRetry(() => import("@/pages/PrivacyPolicy"));
const NotFound = lazyWithRetry(() => import("@/pages/NotFound"));
const UserManual = lazyWithRetry(() => import("@/pages/UserManual"));
const Soporte = lazyWithRetry(() => import("@/pages/Soporte"));

export const routes = (
  <>
                {/* Home — / and /home. Con el flag `aria.home` activo para la
                    empresa, la landing es el ARIA Home (chat-first); si no, el
                    dashboard v2 (Home). /aria-home siempre monta (QA). /dashboard
                    (panel configurable v1, retirado) redirige a Home, que ya cubre
                    el layout configurable vía la misma clave /api/dashboard/config. */}
                <Route path="/" element={<FirmHomeGate />} />

                {/* Checklist "Primeros pasos" como página propia — destino de
                    OnboardingGate cuando el checklist está incompleto y no
                    descartado. Ojo: /onboarding YA está ocupado (ciclo de vida
                    de empleados), por eso el path es /primeros-pasos. */}
                <Route path="/primeros-pasos" element={<PrimerosPasosPage />} />

                <Route path="/home" element={<HomeRoute />} />

                <Route path="/dashboard" element={<Navigate to="/home" replace />} />

                {/* Centro de Datos — pipeline de migración, top-level (fuera del
                    hub contable). Sigue gateado por `accounting.import`. */}
                <Route path="/datos" element={<PermissionGuard permission="accounting.import" fallback={<Navigate to="/" replace />}><Datos /></PermissionGuard>} />

                {/* La Bandeja dejó de ser solo la cola de OCR: dentro vive el
                    archivo documental de la empresa. `documents.view` permite
                    dar acceso al archivo sin dar acceso a la revisión. Va en OR
                    con `ocr.review` a propósito — el permiso nuevo aún no está
                    repartido en los tenants existentes y nadie debe perder
                    acceso por desplegar esto. */}
                <Route path="/bandeja-documentos" element={<PermissionGuard permission={[PERMS.OCR_REVIEW, PERMS.DOCUMENTS_VIEW]} fallback={<Navigate to="/" replace />}><BandejaDocumentos /></PermissionGuard>} />

                {/* AUT-08 — sin guard, esta pantalla montaba para cualquiera: el dato
                    no se fugaba (el backend exige rol en `routes/admin/ocrStats.ts`)
                    pero el usuario veia la pantalla con un error en vez de una
                    redireccion, que es lo que pide la ficha. */}
                <Route path="/configuracion/ocr-stats" element={<PermissionGuard permission={[PERMS.OCR_REVIEW, PERMS.OCR_SCAN]} fallback={<Navigate to="/" replace />}><OcrStats /></PermissionGuard>} />


                {/* Insights fusion (PR4-bis #10) — /insights hosts the Hub:
                    BI Dashboard + Analytics + Resumen ejecutivo as tabs.
                    Legacy routes redirect to ?tab= so bookmarks and AI
                    deep-links keep working. ANALYTICS_VIEW guards the route. */}
                <Route path="/insights" element={
                  <PermissionGuard permission={PERMS.ANALYTICS_VIEW} fallback={<Navigate to="/" replace />}>
                    <InsightsHub />
                  </PermissionGuard>
                } />

                <Route path="/analytics" element={<Navigate to="/insights?tab=analytics" replace />} />

                <Route path="/bi-dashboard" element={<Navigate to="/insights" replace />} />

                <Route path="/executive-summary" element={<Navigate to="/insights?tab=executive" replace />} />

                <Route path="/iese-panel" element={
                  import.meta.env.VITE_ENV === 'production' ? <Navigate to="/" replace /> : <IESEPanel />
                } />


                {/* Configuración & Admin */}
                <Route path="/configuracion" element={<Configuracion />} />

                <Route path="/roles-management" element={
                  <PermissionGuard permission={PERMS.ROLES_MANAGE} fallback={<Navigate to="/" replace />}>
                    <RolesManagement />
                  </PermissionGuard>
                } />

                <Route path="/approvals-dashboard" element={
                  <PermissionGuard permission={PERMS.APPROVALS_VIEW} fallback={<Navigate to="/" replace />}>
                    <ApprovalsDashboard />
                  </PermissionGuard>
                } />

                {/* Backend ya exige admin/owner en POST /api/auth/invite; el guard FE
                    evita renderizar el shell a no-admins (ambos tienen roles.manage). */}
                <Route path="/invite-manager" element={<PermissionGuard permission={PERMS.ROLES_MANAGE} fallback={<Navigate to="/" replace />}><InviteManager /></PermissionGuard>} />


                {/* Legal & Privacy */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />


                {/* Docs */}
                <Route path="/user-manual" element={<UserManual />} />


                {/* Soporte — sin guard: quien puede reportar tiene que poder ver
                    lo suyo. El listado se autolimita por usuario en el backend.
                    La bandeja de ARIA (dominio `support`) enlaza aquí. */}
                <Route path="/soporte" element={<Soporte />} />


                {/* Catch-all — must be the last private route. Renders an
                    elegant placeholder instead of a blank screen or "404". */}
                <Route path="*" element={<NotFound />} />
  </>
);
